import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import { apiError, formatAmount, formatDay } from "../lib/finance";
import {
  FIELD_LABELS, IMPORT_FIELDS, MAX_ROWS, assignColumn, buildRows, decodeFile, fieldForColumn, guessMapping, mappingProblems, parseCsv, templateCsv,
} from "../lib/financeImport";
import { STATEMENT_FIELDS, STATEMENT_SOURCES, guessStatementMapping, statementCurrency, statementLines, statementMappingProblems, statementRows } from "../lib/financeStatement";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, Landmark, CheckCircle2, Copy, Download, FileSpreadsheet, Info, Loader2, Undo2, Upload, XCircle } from "lucide-react";

const IGNORE = "__ignore__";
const NONE = "__none__";

const STATUS = {
  ready: { label: "Prête", icon: CheckCircle2, cls: "text-emerald-700" },
  duplicate: { label: "Doublon possible", icon: Copy, cls: "text-amber-800" },
  error: { label: "À corriger", icon: XCircle, cls: "text-red-700" },
};

function StatusLabel({ status, forced }) {
  const meta = forced ? { label: "Créée quand même", icon: CheckCircle2, cls: "text-emerald-700" } : STATUS[status];
  const Icon = meta.icon;
  return <span className={`inline-flex items-center gap-1 text-xs whitespace-nowrap ${meta.cls}`}><Icon size={13} /> {meta.label}</span>;
}

function duplicateText(d) {
  if (!d) return "";
  return d.kind === "row" ? `Identique à la ligne ${d.line} du fichier.` : `Ressemble à « ${d.title} » (${formatAmount(d.amount, d.currency)}), déjà enregistrée.`;
}

function BatchRow({ batch, onCancel }) {
  const s = batch.summary || {};
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 px-3 py-2.5 text-sm" data-testid="finance-import-batch">
      <div className="min-w-0 flex-1">
        <div className="font-medium text-slate-900 truncate">
          {batch.file_name}
          <span className="ml-2 text-2xs font-medium rounded-full bg-slate-100 text-slate-600 px-2 py-0.5">{batch.kind === "statement" ? "Relevé" : "Tableur"}</span>
        </div>
        <div className="text-xs text-slate-500">
          {formatDay((batch.created_at || "").slice(0, 10))}{batch.created_by_name ? ` · ${batch.created_by_name}` : ""} · {s.created || 0} créée{(s.created || 0) > 1 ? "s" : ""}
          {batch.status === "cancelled" && ` · annulé (${batch.cancel_result?.removed || 0} retirée${(batch.cancel_result?.removed || 0) > 1 ? "s" : ""})`}
        </div>
      </div>
      {batch.status === "active"
        ? <Button variant="outline" size="sm" onClick={() => onCancel(batch)} data-testid="finance-import-cancel"><Undo2 size={14} className="mr-1" /> Annuler l'import</Button>
        : <span className="text-xs text-slate-500">Annulé</span>}
    </div>
  );
}

// Whose account the statement belongs to: every selected line is recorded as paid by this person (or by Jamora).
// Without the global finance view the server only offers the caller's own fiche, as in the expense form.
function StatementOwner({ value, onChange }) {
  const [query, setQuery] = useState("");
  const [people, setPeople] = useState([]);
  useEffect(() => {
    const timer = setTimeout(() => {
      api.get("/finance/payer-candidates", { params: { q: query } }).then(({ data }) => setPeople(data.items)).catch(() => setPeople([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);
  const options = [{ key: "company", label: "Jamora (compte de l'entreprise)", payer: "Jamora" },
    ...people.map((p) => ({ key: p.id, label: `${p.display_name}${p.reference ? ` · ${p.reference}` : ""}`, payer: p.reference || p.display_name }))];
  const known = value && !options.some((o) => o.key === value.key) ? [value] : [];
  return (
    <div className="space-y-1.5">
      <Label>À qui est ce compte ? *</Label>
      <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher un collaborateur" aria-label="Rechercher le titulaire du compte" className="text-base md:text-sm" />
      <Select value={value?.key || NONE} onValueChange={(v) => onChange([...known, ...options].find((o) => o.key === v) || null)}>
        <SelectTrigger data-testid="finance-statement-owner"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>Choisir le titulaire</SelectItem>
          {[...known, ...options].map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
      <p className="text-xs text-slate-500">Chaque ligne retenue sera enregistrée comme payée par ce titulaire, à la date du relevé.</p>
    </div>
  );
}

export default function FinanceImportPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const statement = params.get("type") === "releve";
  const canViewAll = hasPermission(user, "finance:view_all");
  const fileInput = useRef(null);
  const commitKey = useRef(null);
  const [refs, setRefs] = useState({ status: "loading", categories: [], currencies: [], defaultCurrency: "EUR" });
  const [fileName, setFileName] = useState("");
  const [table, setTable] = useState(null);
  const [fileError, setFileError] = useState("");
  const [mapping, setMapping] = useState({});
  const [defaults, setDefaults] = useState({ category: "", currency: "" });
  const [preview, setPreview] = useState(null); // server analysis of the current file + mapping
  const [forced, setForced] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // committed batch
  const [batches, setBatches] = useState({ status: "loading", items: [] });
  const [cancelling, setCancelling] = useState(null);
  const [account, setAccount] = useState({ owner: null, source: "Wave" }); // statement mode only
  const [choices, setChoices] = useState({}); // statement line → {selected, title?, category?}

  const loadBatches = useCallback(() => api.get("/finance/imports")
    .then(({ data }) => setBatches({ status: "ready", items: data.items }))
    .catch(() => setBatches({ status: "error", items: [] })), []);

  useEffect(() => {
    Promise.all([api.get("/finance/categories"), api.get("/finance/settings")])
      .then(([cats, settings]) => setRefs({ status: "ready", categories: cats.data, currencies: settings.data.currencies, defaultCurrency: settings.data.default_currency }))
      .catch((e) => setRefs({ status: "error", categories: [], currencies: [], defaultCurrency: "EUR", error: apiError(e) }));
    loadBatches();
  }, [loadBatches]);

  // Any change to what would be sent invalidates the analysis and the commit key.
  const invalidate = () => { setPreview(null); setForced(new Set()); commitKey.current = null; };

  const readFile = async (file) => {
    setFileError(""); setTable(null); setResult(null); invalidate();
    if (!file) return;
    setFileName(file.name);
    if (file.size > 5 * 1024 * 1024) { setFileError("Fichier trop volumineux (5 Mo maximum)."); return; }
    const parsed = parseCsv(decodeFile(await file.arrayBuffer()));
    if (parsed.length < 2) { setFileError("Le fichier ne contient aucune ligne sous l'en-tête."); return; }
    if (parsed.length - 1 > MAX_ROWS) { setFileError(`${parsed.length - 1} lignes : l'import est limité à ${MAX_ROWS} lignes par fichier. Découpez-le en plusieurs fichiers.`); return; }
    setTable(parsed);
    setMapping(statement ? guessStatementMapping(parsed[0]) : guessMapping(parsed[0]));
    setChoices({});
  };

  const problems = !table ? [] : statement ? statementMappingProblems(mapping) : mappingProblems(mapping);
  const lines = useMemo(() => (statement && table && !statementMappingProblems(mapping).length ? statementLines(table, mapping) : []), [statement, table, mapping]);
  const outgoing = lines.filter((l) => l.outgoing);
  const picked = outgoing.filter((l) => choices[l.line]?.selected).length;
  const rows = useMemo(() => {
    if (!table) return [];
    if (!statement) return buildRows(table, mapping);
    if (!account.owner) return [];
    return statementRows(lines, choices, { payer: account.owner.payer, currency: statementCurrency(account.source, defaults.currency, refs.defaultCurrency), source: account.source });
  }, [table, mapping, statement, lines, choices, account, defaults.currency, refs.defaultCurrency]);
  const setChoice = (line, patch) => { setChoices((c) => ({ ...c, [line]: { ...c[line], ...patch } })); invalidate(); };
  const switchMode = (toStatement) => {
    setParams(toStatement ? { type: "releve" } : {}, { replace: true });
    setTable(null); setFileName(""); setFileError(""); setResult(null); setMapping({}); setChoices({}); invalidate();
  };
  const body = (withForce) => ({
    kind: statement ? "statement" : "spreadsheet",
    file_name: fileName || "import.csv", default_category_id: defaults.category || null, default_currency: defaults.currency || null,
    rows: withForce ? rows.map((r, i) => (forced.has(i) ? { ...r, force: true } : r)) : rows,
  });

  const check = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/finance/imports/preview", body(false));
      setPreview(data);
      setForced(new Set());
      commitKey.current = null;
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const toCreate = preview ? preview.summary.to_create + forced.size : 0;
  const commit = async () => {
    setBusy(true);
    // One key per exact request: a retry after a network error never imports twice.
    if (!commitKey.current) commitKey.current = crypto.randomUUID();
    try {
      const { data } = await api.post("/finance/imports", body(true), { headers: { "Idempotency-Key": commitKey.current } });
      setResult(data);
      setTable(null); setPreview(null); setFileName("");
      toast.success(`Import terminé : ${data.summary.created} dépense${data.summary.created > 1 ? "s" : ""} en brouillon`);
      loadBatches();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const cancelBatch = async () => {
    const batch = cancelling;
    setCancelling(null);
    try {
      const { data } = await api.post(`/finance/imports/${batch.id}/cancel`);
      const kept = data.cancel_result?.kept || [];
      toast.success(`Import annulé : ${data.cancel_result?.removed || 0} brouillon(s) retiré(s)${kept.length ? `, ${kept.length} dépense(s) déjà soumise(s) conservée(s)` : ""}`);
      if (result?.id === batch.id) setResult(data);
      loadBatches();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const download = () => {
    const url = URL.createObjectURL(new Blob([templateCsv()], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url; a.download = "modele-import-depenses.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const toggleForce = (index, on) => setForced((old) => { const next = new Set(old); if (on) next.add(index); else next.delete(index); commitKey.current = null; return next; });

  return (
    <div className="space-y-4" data-testid="finance-import-page">
      <Link to="/finance?tab=expenses" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"><ArrowLeft size={14} /> Retour aux finances</Link>
      <div>
        <h1 className="font-display font-bold text-2xl text-slate-900">Importer des dépenses</h1>
        <p className="text-sm text-slate-500">
          {statement
            ? "Reprenez les paiements d'un relevé de compte (banque, Wave, Orange Money) exporté en CSV : cochez ceux qui concernent Jamora, chacun devient une dépense en brouillon."
            : "Reprenez l'historique d'un tableur (Google Sheet ou Excel enregistré en CSV). Chaque ligne devient une dépense en brouillon."}
        </p>
      </div>
      <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1" role="tablist" aria-label="Type de fichier">
        {[[false, "Tableur de dépenses", FileSpreadsheet], [true, "Relevé de compte", Landmark]].map(([value, label, Icon]) => (
          <button key={label} type="button" role="tab" aria-selected={statement === value} onClick={() => statement !== value && switchMode(value)}
            data-testid={value ? "finance-import-mode-statement" : "finance-import-mode-sheet"}
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm ${statement === value ? "bg-jamora text-white" : "text-slate-600 hover:bg-slate-50"}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-4 items-start">
        <div className="space-y-4 min-w-0">
          {result && (
            <Card className="p-4 sm:p-5 space-y-3 border-emerald-200" data-testid="finance-import-result">
              <div className="flex items-start gap-2">
                <CheckCircle2 size={18} className="text-emerald-600 shrink-0 mt-0.5" />
                <div className="text-sm text-slate-700">
                  <p className="font-medium text-slate-900">{result.status === "cancelled" ? "Import annulé" : "Import terminé"} : {result.file_name}</p>
                  <p>{result.summary.created} dépense{result.summary.created > 1 ? "s" : ""} créée{result.summary.created > 1 ? "s" : ""} en brouillon · {result.summary.duplicates} doublon{result.summary.duplicates > 1 ? "s" : ""} écarté{result.summary.duplicates > 1 ? "s" : ""} · {result.summary.errors} ligne{result.summary.errors > 1 ? "s" : ""} à corriger</p>
                  {result.status === "active" && <p className="text-xs text-slate-500 mt-1">Ajoutez les justificatifs depuis chaque dépense : sans pièce, elles restent marquées « Sans justificatif » et ne peuvent pas être soumises.</p>}
                </div>
              </div>
              {(result.report || []).some((r) => r.status !== "created") && (
                <ul className="text-xs text-slate-600 space-y-0.5 max-h-48 overflow-y-auto" data-testid="finance-import-report">
                  {result.report.filter((r) => r.status !== "created").map((r) => (
                    <li key={r.line}>Ligne {r.line} : {r.status === "duplicate" ? `non créée, ${duplicateText(r.duplicate_of).toLowerCase()}` : r.errors.join(" · ")}</li>
                  ))}
                </ul>
              )}
              <div className="flex flex-wrap gap-2">
                {canViewAll && <Button asChild variant="outline" size="sm"><Link to="/finance?tab=expenses">Voir les dépenses</Link></Button>}
                {result.status === "active" && result.cancellable && <Button variant="outline" size="sm" onClick={() => setCancelling(result)}><Undo2 size={14} className="mr-1" /> Annuler cet import</Button>}
              </div>
            </Card>
          )}

          <Card className="p-4 sm:p-5 space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <Button onClick={() => fileInput.current?.click()} disabled={busy}  data-testid="finance-import-pick"><Upload size={15} className="mr-1.5" /> Choisir un fichier CSV</Button>
              <input ref={fileInput} type="file" accept=".csv,text/csv" className="sr-only" tabIndex={-1} data-testid="finance-import-input"
                onChange={(e) => { readFile(e.target.files?.[0]); e.target.value = ""; }} />
              {!statement && <Button variant="outline" onClick={download}><Download size={15} className="mr-1.5" /> Télécharger le modèle</Button>}
            </div>
            {statement && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1" data-testid="finance-statement-account">
                <StatementOwner value={account.owner} onChange={(owner) => { setAccount((a) => ({ ...a, owner })); invalidate(); }} />
                <div className="space-y-1.5">
                  <Label>Type de compte</Label>
                  <Select value={account.source} onValueChange={(source) => { setAccount((a) => ({ ...a, source })); invalidate(); }}>
                    <SelectTrigger data-testid="finance-statement-source"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATEMENT_SOURCES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">Noté dans la description de chaque dépense, pour retrouver d'où elle vient.</p>
                </div>
              </div>
            )}
            {fileName && <p className="text-sm text-slate-600 flex items-center gap-1.5"><FileSpreadsheet size={15} className="text-slate-400" /> {fileName}{table ? ` · ${table.length - 1} ligne${table.length > 2 ? "s" : ""}` : ""}</p>}
            {fileError && <p role="alert" className="text-sm text-red-700">{fileError}</p>}
            {refs.status === "error" && <p role="alert" className="text-sm text-red-700">{refs.error}</p>}
          </Card>

          {table && (
            <Card className="p-4 sm:p-5 space-y-4" data-testid="finance-import-mapping">
              <div>
                <h3 className="font-display font-semibold text-slate-900">Colonnes</h3>
                <p className="text-xs text-slate-500">Vérifiez à quoi correspond chaque colonne de votre fichier. Les colonnes non reconnues sont ignorées.</p>
              </div>
              <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                {table[0].map((header, index) => (
                  <div key={index} className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_220px] gap-2 px-3 py-2 items-center">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">{header || `Colonne ${index + 1}`}</div>
                      <div className="text-xs text-slate-500 truncate">Ex. : {table.slice(1, 4).map((r) => r[index]).filter(Boolean).join(" · ") || "vide"}</div>
                    </div>
                    <Select value={fieldForColumn(mapping, index) || IGNORE} onValueChange={(v) => { setMapping((m) => assignColumn(m, index, v === IGNORE ? null : v)); invalidate(); }}>
                      <SelectTrigger aria-label={`Champ pour la colonne ${header || index + 1}`} data-testid={`finance-import-col-${index}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={IGNORE}>Ignorer</SelectItem>
                        {(statement ? STATEMENT_FIELDS : IMPORT_FIELDS).map((f) => <SelectItem key={f.key} value={f.key}>{f.label}{f.required ? " *" : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>{statement ? "Catégorie par défaut des lignes retenues" : "Catégorie pour les lignes sans catégorie"}</Label>
                  <Select value={defaults.category || NONE} onValueChange={(v) => { setDefaults((d) => ({ ...d, category: v === NONE ? "" : v })); invalidate(); }}>
                    <SelectTrigger data-testid="finance-import-default-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>Aucune (ligne à corriger)</SelectItem>
                      {refs.categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>{statement ? "Devise du compte" : "Devise pour les lignes sans devise"}</Label>
                  <Select value={defaults.currency || NONE} onValueChange={(v) => { setDefaults((d) => ({ ...d, currency: v === NONE ? "" : v })); invalidate(); }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>{statement ? `Selon le compte (${statementCurrency(account.source, "", refs.defaultCurrency)})` : `Devise par défaut (${refs.defaultCurrency})`}</SelectItem>
                      {refs.currencies.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {problems.length > 0 && <p role="alert" className="text-sm text-red-700">Indiquez {problems.join(" et ")}.</p>}
              {!preview && !statement && (
                <Button onClick={check} disabled={busy || problems.length > 0}  data-testid="finance-import-check">
                  {busy && <Loader2 size={14} className="animate-spin mr-1.5" />} Vérifier les {rows.length} ligne{rows.length > 1 ? "s" : ""}
                </Button>
              )}
            </Card>
          )}

          {statement && table && problems.length === 0 && (
            <Card className="p-4 sm:p-5 space-y-3" data-testid="finance-statement-lines">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                <div>
                  <h3 className="font-display font-semibold text-slate-900">Paiements du relevé</h3>
                  <p className="text-xs text-slate-500">
                    {outgoing.length} sortie{outgoing.length > 1 ? "s" : ""} d'argent{lines.length - outgoing.length > 0 ? ` · ${lines.length - outgoing.length} entrée${lines.length - outgoing.length > 1 ? "s" : ""} ignorée${lines.length - outgoing.length > 1 ? "s" : ""}` : ""}.
                    Cochez uniquement ce qui concerne Jamora ; vous pouvez renommer la dépense et choisir sa catégorie.
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button variant="outline" size="sm" onClick={() => { setChoices((c) => Object.fromEntries(outgoing.map((l) => [l.line, { ...c[l.line], selected: true }]))); invalidate(); }}>Tout cocher</Button>
                  <Button variant="outline" size="sm" onClick={() => { setChoices((c) => Object.fromEntries(Object.entries(c).map(([k, v]) => [k, { ...v, selected: false }]))); invalidate(); }}>Tout décocher</Button>
                </div>
              </div>
              {!outgoing.length ? <p className="text-sm text-slate-500">Aucune sortie d'argent trouvée : vérifiez les colonnes « Montant » ou « Débit ».</p> : (
                <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 max-h-[60vh] overflow-y-auto">
                  {outgoing.map((l) => {
                    const choice = choices[l.line] || {};
                    return (
                      <li key={l.line} className={`px-3 py-2.5 text-sm ${choice.selected ? "bg-slate-50" : ""}`} data-testid="finance-statement-line">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <Checkbox checked={!!choice.selected} onCheckedChange={(v) => setChoice(l.line, { selected: v === true })} className="mt-0.5" data-testid={`finance-statement-pick-${l.line}`} />
                          <span className="min-w-0 flex-1">
                            <span className="flex justify-between gap-3">
                              <span className="text-slate-900 break-words">{l.title}</span>
                              <span className="tabular-nums font-semibold whitespace-nowrap">−{l.amount}</span>
                            </span>
                            <span className="block text-xs text-slate-500">{[l.date, l.label !== l.title && l.label, l.reference && `réf. ${l.reference}`].filter(Boolean).join(" · ")}</span>
                          </span>
                        </label>
                        {choice.selected && (
                          <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_200px] gap-2 mt-2 pl-7">
                            <Input value={choice.title ?? l.title} onChange={(e) => setChoice(l.line, { title: e.target.value })} maxLength={180}
                              aria-label={`Libellé de la dépense, ligne ${l.line}`} className="text-base md:text-sm h-9" data-testid={`finance-statement-title-${l.line}`} />
                            <Select value={choice.category || NONE} onValueChange={(v) => setChoice(l.line, { category: v === NONE ? undefined : v })}>
                              <SelectTrigger className="h-9" aria-label={`Catégorie, ligne ${l.line}`} data-testid={`finance-statement-category-${l.line}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value={NONE}>Catégorie par défaut</SelectItem>
                                {refs.categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
              {!account.owner && <p role="alert" className="text-sm text-red-700">Choisissez à qui est ce compte, en haut de la page.</p>}
              {!preview && (
                <Button onClick={check} disabled={busy || !account.owner || rows.length === 0}  data-testid="finance-import-check">
                  {busy && <Loader2 size={14} className="animate-spin mr-1.5" />} Vérifier {picked} paiement{picked > 1 ? "s" : ""} coché{picked > 1 ? "s" : ""}
                </Button>
              )}
            </Card>
          )}

          {preview && (
            <Card className="p-4 sm:p-5 space-y-3" data-testid="finance-import-preview">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm text-slate-700" data-testid="finance-import-summary">
                  <strong>{preview.summary.rows}</strong> ligne{preview.summary.rows > 1 ? "s" : ""} ·{" "}
                  <span className="text-emerald-700">{toCreate} à créer</span>
                  {preview.summary.duplicates - forced.size > 0 && <> · <span className="text-amber-800">{preview.summary.duplicates - forced.size} doublon{preview.summary.duplicates - forced.size > 1 ? "s" : ""} écarté{preview.summary.duplicates - forced.size > 1 ? "s" : ""}</span></>}
                  {preview.summary.errors > 0 && <> · <span className="text-red-700">{preview.summary.errors} à corriger dans le fichier</span></>}
                </div>
                <Button onClick={commit} disabled={busy || toCreate === 0}  data-testid="finance-import-run">
                  {busy && <Loader2 size={14} className="animate-spin mr-1.5" />} Importer {toCreate} dépense{toCreate > 1 ? "s" : ""}
                </Button>
              </div>
              <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 max-h-[60vh] overflow-y-auto">
                {preview.rows.map((r) => (
                  <li key={r.index} className="px-3 py-2.5 text-sm flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-3" data-testid="finance-import-row">
                    <span className="text-xs text-slate-400 w-14 shrink-0">Ligne {r.line}</span>
                    <span className="min-w-0 flex-1">
                      {r.expense ? (
                        <>
                          <span className="flex justify-between gap-3">
                            <span className="font-medium text-slate-900 break-words">{r.expense.title}</span>
                            <span className="tabular-nums font-semibold whitespace-nowrap">{formatAmount(r.expense.amount, r.expense.currency)}</span>
                          </span>
                          <span className="block text-xs text-slate-500">
                            {[r.expense.incurred_at && formatDay(r.expense.incurred_at), r.expense.category_name, r.expense.supplier,
                              r.expense.payer ? `payé par ${r.expense.payer}` : "non payée", r.expense.preconstitution && "avant constitution"].filter(Boolean).join(" · ")}
                          </span>
                        </>
                      ) : <span className="text-slate-500">{rows[r.index]?.title || "Ligne sans libellé"}</span>}
                      {r.errors.length > 0 && <span className="block text-xs text-red-700 mt-0.5">{r.errors.join(" · ")}</span>}
                      {r.warnings.length > 0 && <span className="block text-xs text-amber-800 mt-0.5">{r.warnings.join(" · ")}</span>}
                      {r.status === "duplicate" && (
                        <span className="block text-xs text-amber-800 mt-0.5">
                          {duplicateText(r.duplicate_of)}
                          <label className="flex items-center gap-1.5 mt-1 text-slate-700">
                            <Checkbox checked={forced.has(r.index)} onCheckedChange={(v) => toggleForce(r.index, v === true)} data-testid={`finance-import-force-${r.line}`} />
                            Ce n'est pas un doublon : créer quand même
                          </label>
                        </span>
                      )}
                    </span>
                    <span className="shrink-0"><StatusLabel status={r.status} forced={forced.has(r.index)} /></span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-slate-500">Pour corriger une ligne, modifiez le fichier puis choisissez-le à nouveau.</p>
            </Card>
          )}

          <Card className="overflow-hidden">
            <div className="px-4 pt-4 pb-2">
              <h3 className="font-display font-semibold text-slate-900">Imports précédents</h3>
              <p className="text-xs text-slate-500">Annuler un import retire ses dépenses encore en brouillon ; celles déjà soumises sont conservées.</p>
            </div>
            {batches.status === "loading" ? <div className="p-4"><Skeleton className="h-10 w-full" /></div>
              : batches.status === "error" ? <p className="px-4 pb-4 text-sm text-slate-600">Impossible de charger les imports. <button className="underline" onClick={loadBatches}>Réessayer</button></p>
              : !batches.items.length ? <p className="px-4 pb-4 text-sm text-slate-500">Aucun import pour le moment.</p>
              : <div className="divide-y divide-slate-100 border-t border-slate-100">{batches.items.map((b) => <BatchRow key={b.id} batch={b} onCancel={setCancelling} />)}</div>}
          </Card>
        </div>

        <aside className="space-y-4 lg:sticky lg:top-4">
          {statement && (
            <Card className="p-4 space-y-2 bg-slate-50 border-slate-200">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-900"><Info size={15} className="text-jamora" /> Relevé de compte</div>
              <p className="text-xs text-slate-600">Exportez le relevé en CSV depuis votre banque ou votre application (Wave, Orange Money). Seules les sorties d'argent sont proposées ; les entrées sont ignorées.</p>
              <p className="text-xs text-slate-600">L'ID de transaction sert à reconnaître un paiement déjà importé : réimporter le même relevé ne crée pas de doublon.</p>
            </Card>
          )}
          {!statement && <Card className="p-4 space-y-2 bg-slate-50 border-slate-200">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-900"><Info size={15} className="text-jamora" /> Colonnes reconnues</div>
            <p className="text-xs text-slate-600">{IMPORT_FIELDS.map((f) => f.label + (f.required ? " *" : "")).join(", ")}. Seuls le libellé et le montant sont obligatoires.</p>
            <p className="text-xs text-slate-600"><strong>{FIELD_LABELS.paid_by}</strong> : un nom de l'annuaire (ou sa référence COL-…), « Jamora » pour l'entreprise, sinon la personne est enregistrée comme tiers. La date sert alors de date de paiement.</p>
          </Card>}
          <Card className="p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-900"><AlertTriangle size={15} className="text-jamora" /> Doublons et justificatifs</div>
            <p className="text-xs text-slate-600">Une ligne qui ressemble à une dépense déjà enregistrée (même référence de facture, ou même date et même montant avec le même fournisseur ou le même libellé) est écartée, sauf si vous cochez « créer quand même ».</p>
            <p className="text-xs text-slate-600">Les dépenses importées arrivent sans justificatif : ajoutez-le depuis chaque dépense avant de la soumettre.</p>
          </Card>
        </aside>
      </div>

      <AlertDialog open={!!cancelling} onOpenChange={(open) => !open && setCancelling(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Annuler l'import « {cancelling?.file_name} » ?</AlertDialogTitle>
            <AlertDialogDescription>Ses dépenses encore en brouillon seront retirées, y compris celles que vous avez modifiées depuis. Les dépenses déjà soumises sont conservées. L'historique garde la trace de l'import et de son annulation.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Garder l'import</AlertDialogCancel>
            <AlertDialogAction onClick={cancelBatch} className="bg-red-600 hover:bg-red-700" data-testid="finance-import-cancel-confirm">Annuler l'import</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
